import React, { useEffect, useState } from "react";
import Layout from "../layout/index";
import axios from "axios";
import Swal from "sweetalert2";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { ROOT_API } from "../../api/config";
import { ErrorCode } from "../../api/ErrorCode";
import { formatDate } from "../../utils/dates";
import { isPremiumUser } from "../../utils/premium";
import {
  updateUserInformation,
  clearUserInformation,
} from "../../stores/users/userSlice";
import "./style.scss";

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("access_token")}`,
});

const CHANNELS = ["email", "sms", "both"];

const Account = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector((state) => state.user);
  const selectedLocale =
    useSelector((state) => state.lang.value) ||
    localStorage.getItem("lang") ||
    "hr";
  const locale = selectedLocale === "en" ? "en-US" : "hr-HR";

  const isPremium = isPremiumUser(user);
  const tier = isPremium ? (user?.plan === "business" ? "business" : "premium") : "free";
  // Enterprise tier (gates the daily all-clear digest toggle below).
  const isEnterprise =
    isPremium && ["enterprise", "enterprise_concierge"].includes(user?.plan);

  // Editable profile fields, seeded from the store.
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [channel, setChannel] = useState("email");
  const [digest, setDigest] = useState("off");
  const [savingDigest, setSavingDigest] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingRenew, setSavingRenew] = useState(false);
  // Default on: a missing flag (stale session before this field existed) reads as renewing.
  const autoRenew = user?.auto_renew !== false;

  useEffect(() => {
    setName(user?.full_name || "");
    setPhone(user?.phone_number || "");
    setChannel(user?.default_channel || "email");
    setDigest(user?.daily_digest_channel || "off");
  }, [
    user?.email,
    user?.full_name,
    user?.phone_number,
    user?.default_channel,
    user?.daily_digest_channel,
  ]);

  // Hydrate the store from /user/me on mount: a fresh login only carries email + name, but
  // this page needs plan/premium_until/auto_renew/phone_verified to render correctly.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${ROOT_API}apis/user/me`, {
          headers: authHeaders(),
        });
        if (!cancelled && res?.data?.data)
          dispatch(updateUserInformation(res.data.data));
      } catch (e) {
        /* keep whatever the store already has */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  const t = (id, values) => intl.formatMessage({ id }, values);

  const showError = (error) => {
    const code = error?.response?.data?.error_code;
    Swal.fire({
      icon: "error",
      title: "Oops...",
      text:
        code && ErrorCode[code]
          ? t(ErrorCode[code])
          : t("there_are_error_occured"),
    });
  };

  const logout = () => {
    localStorage.removeItem("access_token");
    dispatch(clearUserInformation());
    navigate("/login");
  };

  // ---- Profile (name / phone / default channel) ----
  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const res = await axios.post(
        `${ROOT_API}apis/update-profile`,
        { full_name: name, phone_number: phone, default_channel: channel },
        { headers: authHeaders() }
      );
      if (res?.data?.data) dispatch(updateUserInformation(res.data.data));
      Swal.fire({
        icon: "success",
        title: t("account_saved"),
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (error) {
      showError(error);
    } finally {
      setSavingProfile(false);
    }
  };

  // ---- Auto-renewal opt-out ----
  const setAutoRenew = async (enabled) => {
    if (savingRenew) return;
    setSavingRenew(true);
    try {
      const res = await axios.post(
        `${ROOT_API}apis/update-profile`,
        { auto_renew: enabled },
        { headers: authHeaders() }
      );
      if (res?.data?.data) dispatch(updateUserInformation(res.data.data));
      Swal.fire({
        icon: "success",
        title: t(enabled ? "account_renew_on_done" : "account_renew_off_done"),
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (error) {
      showError(error);
    } finally {
      setSavingRenew(false);
    }
  };

  const toggleAutoRenew = () => {
    if (savingRenew) return;
    if (!autoRenew) {
      setAutoRenew(true);
      return;
    }
    Swal.fire({
      icon: "warning",
      title: t("account_renew_off_confirm_title"),
      text: t("account_renew_off_confirm_text", {
        date: user?.premium_until ? formatDate(user.premium_until, locale) : "",
      }),
      showCancelButton: true,
      confirmButtonText: t("account_renew_off_confirm_yes"),
      cancelButtonText: t("account_cancel"),
      confirmButtonColor: "#a63c2a",
    }).then((r) => {
      if (r.isConfirmed) setAutoRenew(false);
    });
  };

  // ---- Daily all-clear digest channel (Enterprise) ----
  const saveDigest = async (value) => {
    if (savingDigest || value === digest) return;
    setDigest(value);
    setSavingDigest(true);
    try {
      const res = await axios.post(
        `${ROOT_API}apis/update-profile`,
        { daily_digest_channel: value },
        { headers: authHeaders() }
      );
      if (res?.data?.data) dispatch(updateUserInformation(res.data.data));
    } catch (error) {
      setDigest(user?.daily_digest_channel || "off");
      showError(error);
    } finally {
      setSavingDigest(false);
    }
  };

  // ---- Phone verification for SMS (paid users; the verify SMS is free) ----
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneBusy, setPhoneBusy] = useState(false);

  const startPhoneVerify = async () => {
    setPhoneBusy(true);
    try {
      await axios.post(
        `${ROOT_API}apis/request-phone-verification`,
        {},
        { headers: { ...authHeaders(), lang: selectedLocale } }
      );
      setPhoneCode("");
      setPhoneOpen(true);
    } catch (error) {
      showError(error);
    } finally {
      setPhoneBusy(false);
    }
  };

  const confirmPhoneVerify = async () => {
    setPhoneBusy(true);
    try {
      await axios.post(
        `${ROOT_API}apis/confirm-phone-verification`,
        { otp: phoneCode.trim() },
        { headers: authHeaders() }
      );
      setPhoneOpen(false);
      dispatch(updateUserInformation({ phone_verified: true }));
      Swal.fire({
        icon: "success",
        title: t("account_phone_verify_success"),
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (error) {
      showError(error);
    } finally {
      setPhoneBusy(false);
    }
  };

  // ---- Email change (two-step: password -> code to new address) ----
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailStep, setEmailStep] = useState("request");
  const [newEmail, setNewEmail] = useState("");
  const [emailPw, setEmailPw] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);

  const openEmail = () => {
    setEmailStep("request");
    setNewEmail("");
    setEmailPw("");
    setEmailCode("");
    setEmailOpen(true);
  };

  const requestEmailChange = async () => {
    setEmailBusy(true);
    try {
      await axios.post(
        `${ROOT_API}apis/request-email-change`,
        { new_email: newEmail.trim(), password: emailPw },
        { headers: authHeaders() }
      );
      setEmailStep("confirm");
    } catch (error) {
      showError(error);
    } finally {
      setEmailBusy(false);
    }
  };

  const confirmEmailChange = async () => {
    setEmailBusy(true);
    try {
      await axios.post(
        `${ROOT_API}apis/confirm-email-change`,
        { otp: emailCode.trim() },
        { headers: authHeaders() }
      );
      setEmailOpen(false);
      await Swal.fire({
        icon: "success",
        title: t("account_email_changed"),
        confirmButtonText: "OK",
      });
      logout();
    } catch (error) {
      showError(error);
    } finally {
      setEmailBusy(false);
    }
  };

  // ---- Password change ----
  const [pwOpen, setPwOpen] = useState(false);
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  const openPw = () => {
    setOldPw("");
    setNewPw("");
    setConfirmPw("");
    setPwOpen(true);
  };

  const submitPw = async () => {
    if (newPw !== confirmPw) {
      Swal.fire({ icon: "error", title: "Oops...", text: t("password_not_match") });
      return;
    }
    setPwBusy(true);
    try {
      await axios.post(
        `${ROOT_API}apis/change-password`,
        { old_password: oldPw, new_password: newPw, confirm_new_password: confirmPw },
        { headers: { ...authHeaders(), lang: selectedLocale } }
      );
      setPwOpen(false);
      Swal.fire({
        icon: "success",
        title: t("change_password_success"),
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (error) {
      showError(error);
    } finally {
      setPwBusy(false);
    }
  };

  // ---- Delete account ----
  const [delOpen, setDelOpen] = useState(false);
  const [delPw, setDelPw] = useState("");
  const [delBusy, setDelBusy] = useState(false);

  const submitDelete = async () => {
    setDelBusy(true);
    try {
      await axios.post(
        `${ROOT_API}apis/delete-account`,
        { password: delPw },
        { headers: authHeaders() }
      );
      setDelOpen(false);
      await Swal.fire({
        icon: "success",
        title: t("account_deleted"),
        confirmButtonText: "OK",
      });
      logout();
    } catch (error) {
      showError(error);
    } finally {
      setDelBusy(false);
    }
  };

  const smsLocked = !isPremium;

  return (
    <Layout>
      <div className="account">
        <div className="container">
          <header className="account__head">
            <h1 className="account__title">{t("account_title")}</h1>
            <p className="account__subtitle">{t("account_subtitle")}</p>
          </header>

          {/* Personal details */}
          <section className="account__card">
            <h2 className="account__card-title">{t("account_profile_heading")}</h2>

            <label className="account__field">
              <span className="account__label">{t("account_name_label")}</span>
              <input
                className="account__input"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>

            <label className="account__field">
              <span className="account__label">{t("account_phone_label")}</span>
              <input
                className="account__input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+385…"
              />
              <span className="account__hint">{t("account_phone_optional_hint")}</span>
            </label>

            <div className="account__field">
              <span className="account__label">{t("account_channel_label")}</span>
              <div className="account__segmented">
                {CHANNELS.map((c) => {
                  const disabled = c !== "email" && smsLocked;
                  return (
                    <button
                      key={c}
                      type="button"
                      className={`account__seg${channel === c ? " is-active" : ""}`}
                      disabled={disabled}
                      onClick={() => setChannel(c)}
                    >
                      {t(`account_channel_${c}`)}
                    </button>
                  );
                })}
              </div>
              {smsLocked ? (
                <span className="account__hint">{t("account_channel_sms_premium")}</span>
              ) : null}
            </div>

            <button
              type="button"
              className="btn-cta account__save"
              onClick={saveProfile}
              disabled={savingProfile}
            >
              {t("account_save")}
            </button>
          </section>

          {/* Subscription */}
          <section className="account__card">
            <h2 className="account__card-title">{t("account_sub_heading")}</h2>
            <div className="account__sub-row">
              <span className={`account__badge account__badge--${tier}`}>
                {tier === "free"
                  ? t("account_sub_free")
                  : tier === "business"
                  ? "Business"
                  : "Premium"}
              </span>
              {tier !== "free" && user?.premium_until ? (
                <span className="account__sub-until">
                  {t("account_sub_active_until", {
                    date: formatDate(user.premium_until, locale),
                  })}
                </span>
              ) : null}
            </div>
            {tier !== "free" && user?.premium_until ? (
              <div className={`account__renew${autoRenew ? "" : " account__renew--off"}`}>
                <span className="account__renew-state">
                  {autoRenew
                    ? t("account_renew_on", {
                        date: formatDate(user.premium_until, locale),
                      })
                    : t("account_renew_off", {
                        date: formatDate(user.premium_until, locale),
                      })}
                </span>
                <button
                  type="button"
                  className="account__renew-toggle"
                  onClick={toggleAutoRenew}
                  disabled={savingRenew}
                >
                  {t(autoRenew ? "account_renew_turn_off" : "account_renew_turn_on")}
                </button>
              </div>
            ) : null}
            <button
              type="button"
              className="account__btn account__btn--primary"
              onClick={() => navigate("/pretplata")}
            >
              {tier === "free"
                ? t("account_sub_go_premium")
                : tier === "premium"
                ? t("account_sub_upgrade")
                : t("account_sub_manage")}
            </button>
          </section>

          {/* Phone verification for SMS — paid users only */}
          {isPremium ? (
            <section className="account__card">
              <h2 className="account__card-title">
                {t("account_phone_verify_heading")}
              </h2>
              {user?.phone_verified ? (
                <p className="account__hint">
                  ✓ {t("account_phone_verify_verified")}
                </p>
              ) : !user?.phone_number ? (
                <p className="account__hint">{t("account_phone_verify_need_number")}</p>
              ) : (
                <>
                  <p className="account__hint">{t("account_phone_verify_intro")}</p>
                  <p className="account__hint">
                    {t("account_phone_verify_free_note")}
                  </p>
                  <button
                    type="button"
                    className="btn-cta account__save"
                    onClick={startPhoneVerify}
                    disabled={phoneBusy}
                  >
                    {t("account_phone_verify_btn")}
                  </button>
                </>
              )}
            </section>
          ) : null}

          {/* Daily all-clear digest — Enterprise only */}
          {isEnterprise ? (
            <section className="account__card">
              <h2 className="account__card-title">{t("account_digest_heading")}</h2>
              <p className="account__hint">{t("account_digest_intro")}</p>
              <div className="account__segmented">
                {["off", "email", "sms", "both"].map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`account__seg${digest === c ? " is-active" : ""}`}
                    disabled={savingDigest}
                    onClick={() => saveDigest(c)}
                  >
                    {t(c === "off" ? "account_digest_off" : `account_channel_${c}`)}
                  </button>
                ))}
              </div>
            </section>
          ) : null}

          {/* Email + password */}
          <section className="account__card">
            <h2 className="account__card-title">{t("account_email_heading")}</h2>
            <div className="account__readonly">
              <span className="account__label">{t("account_email_current")}</span>
              <span className="account__value">{user?.email}</span>
            </div>
            <button type="button" className="account__btn" onClick={openEmail}>
              {t("account_email_change_btn")}
            </button>

            <hr className="account__divider" />

            <h2 className="account__card-title">{t("account_password_heading")}</h2>
            <p className="account__hint">{t("account_password_hint")}</p>
            <button type="button" className="account__btn" onClick={openPw}>
              {t("change_password")}
            </button>
          </section>

          {/* Danger zone */}
          <section className="account__card account__card--danger">
            <h2 className="account__card-title">{t("account_danger_heading")}</h2>
            <p className="account__hint">{t("account_danger_text")}</p>
            <button
              type="button"
              className="account__btn account__btn--danger"
              onClick={() => {
                setDelPw("");
                setDelOpen(true);
              }}
            >
              {t("account_delete_btn")}
            </button>
          </section>
        </div>
      </div>

      {/* Email change dialog */}
      <Dialog open={emailOpen} onClose={() => setEmailOpen(false)}>
        <DialogTitle>{t("account_email_change_btn")}</DialogTitle>
        <DialogContent>
          <div className="account__dialog">
            {emailStep === "request" ? (
              <>
                <label className="account__field">
                  <span className="account__label">{t("account_email_new_label")}</span>
                  <input
                    className="account__input"
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                  />
                </label>
                <label className="account__field">
                  <span className="account__label">{t("account_email_pw_label")}</span>
                  <input
                    className="account__input"
                    type="password"
                    value={emailPw}
                    onChange={(e) => setEmailPw(e.target.value)}
                  />
                </label>
              </>
            ) : (
              <>
                <p className="account__hint">
                  {t("account_email_code_sent", { email: newEmail })}
                </p>
                <label className="account__field">
                  <span className="account__label">{t("account_email_code_label")}</span>
                  <input
                    className="account__input"
                    value={emailCode}
                    onChange={(e) => setEmailCode(e.target.value)}
                  />
                </label>
              </>
            )}
          </div>
        </DialogContent>
        <DialogActions style={{ padding: "0 24px 16px" }}>
          <button className="account__btn" onClick={() => setEmailOpen(false)}>
            {t("account_cancel")}
          </button>
          {emailStep === "request" ? (
            <button
              className="btn-cta"
              onClick={requestEmailChange}
              disabled={emailBusy || !newEmail || !emailPw}
            >
              {t("account_email_send_code")}
            </button>
          ) : (
            <button
              className="btn-cta"
              onClick={confirmEmailChange}
              disabled={emailBusy || !emailCode}
            >
              {t("account_email_confirm")}
            </button>
          )}
        </DialogActions>
      </Dialog>

      {/* Password change dialog */}
      <Dialog open={pwOpen} onClose={() => setPwOpen(false)}>
        <DialogTitle>{t("change_password")}</DialogTitle>
        <DialogContent>
          <div className="account__dialog">
            <input
              className="account__input mb-12"
              type="password"
              placeholder={t("old_password")}
              value={oldPw}
              onChange={(e) => setOldPw(e.target.value)}
            />
            <input
              className="account__input mb-12"
              type="password"
              placeholder={t("new_password")}
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
            />
            <input
              className="account__input"
              type="password"
              placeholder={t("confirm_new_password")}
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
            />
          </div>
        </DialogContent>
        <DialogActions style={{ padding: "0 24px 16px" }}>
          <button className="account__btn" onClick={() => setPwOpen(false)}>
            {t("account_cancel")}
          </button>
          <button
            className="btn-cta"
            onClick={submitPw}
            disabled={pwBusy || !oldPw || !newPw || !confirmPw}
          >
            {t("submit")}
          </button>
        </DialogActions>
      </Dialog>

      {/* Delete account dialog */}
      <Dialog open={delOpen} onClose={() => setDelOpen(false)}>
        <DialogTitle>{t("account_delete_confirm_title")}</DialogTitle>
        <DialogContent>
          <div className="account__dialog">
            <p className="account__hint">{t("account_delete_confirm_text")}</p>
            <input
              className="account__input"
              type="password"
              placeholder={t("account_email_pw_label")}
              value={delPw}
              onChange={(e) => setDelPw(e.target.value)}
            />
          </div>
        </DialogContent>
        <DialogActions style={{ padding: "0 24px 16px" }}>
          <button className="account__btn" onClick={() => setDelOpen(false)}>
            {t("account_cancel")}
          </button>
          <button
            className="account__btn account__btn--danger"
            onClick={submitDelete}
            disabled={delBusy || !delPw}
          >
            {t("account_delete_btn")}
          </button>
        </DialogActions>
      </Dialog>

      {/* Phone verification dialog */}
      <Dialog open={phoneOpen} onClose={() => setPhoneOpen(false)}>
        <DialogTitle>{t("account_phone_verify_heading")}</DialogTitle>
        <DialogContent>
          <div className="account__dialog">
            <p className="account__hint">
              {t("account_phone_verify_code_sent", {
                phone: user?.phone_number || phone,
              })}
            </p>
            <label className="account__field">
              <span className="account__label">
                {t("account_phone_verify_code_label")}
              </span>
              <input
                className="account__input"
                value={phoneCode}
                onChange={(e) => setPhoneCode(e.target.value)}
              />
            </label>
          </div>
        </DialogContent>
        <DialogActions style={{ padding: "0 24px 16px" }}>
          <button className="account__btn" onClick={() => setPhoneOpen(false)}>
            {t("account_cancel")}
          </button>
          <button
            className="btn-cta"
            onClick={confirmPhoneVerify}
            disabled={phoneBusy || !phoneCode}
          >
            {t("account_phone_verify_confirm")}
          </button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Account;
